<script setup lang="ts">
import { $updateKopItem } from '@/api/kop'
import { useSnackbarStore } from '@/store/snackbar'
import type { KopItem } from '@/models/kop/kopItem'

interface IProps {
  modelValue?: boolean,
  kopItem?: KopItem,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  kopItem: () => ({}),
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'updated', value: KopItem): void
}

const emit = defineEmits<Emit>()
const snackbarStore = useSnackbarStore()

const editedMainFullCode = ref<string | null>(null)
const updateCodeLoading = ref(false)

const showEditCodeDialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const restoreInitialCode = () => {
  editedMainFullCode.value = props.kopItem?.initial_main_full_code ?? null
}

const updateMainFullCode = async () => {
  if (!props.kopItem?.id) return

  updateCodeLoading.value = true
  try {
    const res = await $updateKopItem(props.kopItem.id, {
      main_full_code: editedMainFullCode.value,
    })
    emit('updated', res.data)
    showEditCodeDialog.value = false
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item Code updated successfully!',
    })
  } catch (e: any) {
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`,
    })
  } finally {
    updateCodeLoading.value = false
  }
}

watch(() => props.modelValue, isVisible => {
  if (isVisible) {
    editedMainFullCode.value = props.kopItem?.main_full_code ?? props.kopItem?.kop_item_code ?? null
  }
})
</script>

<template>
  <VDialog
    v-model="showEditCodeDialog"
    max-width="520"
  >
    <VCard :loading="updateCodeLoading">
      <VCardTitle>Edit KOP Item Code</VCardTitle>
      <VCardText>
        <VTextField
          v-model="editedMainFullCode"
          label="KOP Item Code"
          autofocus
        >
          <template #append-inner>
            <VBtn
              icon
              size="x-small"
              variant="text"
              :disabled="!kopItem.initial_main_full_code"
              @click="restoreInitialCode"
            >
              <VIcon icon="tabler-restore" />
              <VMenu
                activator="parent"
                location="top center"
                open-on-hover
                open-delay="100"
                close-delay="0"
                offset="1"
                class="default-tooltip"
              >
                <span class="px-2">Restore initial code</span>
              </VMenu>
            </VBtn>
          </template>
        </VTextField>
      </VCardText>
      <VCardActions class="d-flex align-center justify-space-between">
        <VBtn
          class="px-3"
          :disabled="updateCodeLoading"
          variant="elevated"
          color="secondary"
          @click="showEditCodeDialog = false"
        >
          Cancel
        </VBtn>
        <VBtn
          class="px-3"
          variant="elevated"
          :loading="updateCodeLoading"
          @click="updateMainFullCode"
        >
          Update
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
